import { useRef, useState } from 'react';
import { CAPABILITY_SCOPES } from '@/auth/scopes';
import {
  listAllSitesUsage, usageEntryOwners, getSitePermissionOwners,
  type SiteOwner, type SiteUsageEntry,
} from '@/graph/modules/sites';
import { aggregateSiteOwners, ownersToCsv, type SiteOwnersRecord, type SiteOwnersResult } from './siteOwners';
import { useSession } from '@/store/session';
import { Panel } from '@/components/Panel';
import { ModuleGate } from '@/components/ModuleGate';
import { Metric, MetricGrid } from '@/components/Metric';
import { ProgressBar } from '@/components/ProgressBar';
import { ErrorNotice } from '@/components/ErrorNotice';
import { IconGrid } from '@/components/icons';
import { downloadBlob, dateStamp } from '@/compliance/evidence/exporters/download';

type Status = 'idle' | 'enumerating' | 'scanning' | 'done' | 'error';

/** Sites processed in parallel per wave when the SharePoint-owners opt-in is on. */
const CONCURRENCY = 8;

function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** A readable site name from its URL (the usage report gives no title). */
function siteNameFromUrl(url: string): string {
  if (!url) return '';
  try {
    const u = new URL(url);
    const segs = u.pathname.split('/').filter(Boolean);
    const last = segs[segs.length - 1];
    return last ? decodeURIComponent(last) : u.hostname;
  } catch {
    return url;
  }
}

/** Short badge label for an owner's identity type. */
function ownerTypeLabel(t: SiteOwner['type']): string {
  switch (t) {
    case 'user': return 'user';
    case 'group': return 'group';
    case 'siteGroup': return 'site group';
    case 'application': return 'app';
    default: return 'owner';
  }
}

/** Render an owner list as chips, or a muted dash when empty. */
function ownerChips(list: SiteOwner[]) {
  if (list.length === 0) return <span className="muted">—</span>;
  return (
    <span style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {list.map((o, i) => (
        <span key={`${o.type}:${o.id ?? o.displayName}:${i}`} className="chip" title={o.email ?? ownerTypeLabel(o.type)}>
          {o.displayName} <span className="muted">· {ownerTypeLabel(o.type)}</span>
        </span>
      ))}
    </span>
  );
}

/**
 * C14 — Site owners inventory. Lists EVERY SharePoint site in the tenant — group and
 * non-group — with its URL and primary owner, from the SharePoint site usage report
 * (delegated Reports.Read.All, read as JSON so all traffic stays on graph.microsoft.com).
 *
 * The richer SharePoint "Site Owners" column is a SEPARATE opt-in (C15) needing
 * Sites.FullControl.All (the only scope that can read a site's permissions); it is
 * never requested at connect, only when the user enables it. Read-only; exports to CSV.
 */
export function SiteOwnersPanel() {
  const siteOwnersOptedIn = useSession((s) => s.hasConsent('C15'));
  const grantSiteOwners = useSession((s) => s.grantConsent);

  const [status, setStatus] = useState<Status>('idle');
  const [nameFilter, setNameFilter] = useState('');
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);
  const [result, setResult] = useState<SiteOwnersResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  const cancelRef = useRef(false);

  /** Build owner records for the sites with bounded concurrency + progress. */
  async function collectRecords(entries: readonly SiteUsageEntry[]) {
    const records: SiteOwnersRecord[] = [];
    let completed = 0;
    for (const wave of chunk(entries, CONCURRENCY)) {
      if (cancelRef.current) break;
      const settled = await Promise.all(
        wave.map(async (e): Promise<SiteOwnersRecord> => {
          const owners = usageEntryOwners(e);
          // Per-site SharePoint owners are read only when the C15 opt-in is on.
          const siteOwners = siteOwnersOptedIn
            ? await getSitePermissionOwners(e.siteId).catch(() => [] as SiteOwner[])
            : [];
          return {
            site: { id: e.siteId, displayName: siteNameFromUrl(e.siteUrl) || e.siteUrl || e.siteId, webUrl: e.siteUrl },
            owners,
            siteOwners,
          };
        }),
      );
      records.push(...settled);
      completed += wave.length;
      setDone(completed);
    }
    return records;
  }

  async function scan() {
    cancelRef.current = false;
    setResult(null);
    setError(null);
    setDone(0);
    setTotal(0);
    setStatus('enumerating');
    try {
      const all = await listAllSitesUsage('D30');
      if (cancelRef.current) { setStatus('idle'); return; }

      const needle = nameFilter.trim().toLowerCase();
      const entries = needle ? all.filter((e) => e.siteUrl.toLowerCase().includes(needle)) : all;

      setTotal(entries.length);
      setStatus('scanning');
      const records = await collectRecords(entries);
      setResult(aggregateSiteOwners(records));
      setStatus('done');
    } catch (e) {
      setError(e);
      setStatus('error');
    }
  }

  function exportCsv() {
    if (!result) return;
    downloadBlob(
      `site-owners-${dateStamp(new Date().toISOString())}.csv`,
      'text/csv;charset=utf-8',
      ownersToCsv(result.records),
    );
  }

  const busy = status === 'scanning' || status === 'enumerating';

  return (
    <Panel id="c14" icon={<IconGrid />} title="Site owners inventory" cap="C14"
      why={CAPABILITY_SCOPES.C14.why}>
      <ModuleGate cap="C14">
        <div className="notice notice--info">
          <strong>Seeing random IDs instead of URLs / owners?</strong>
          <p>Microsoft conceals report data by default. A Global Admin can reveal it: Microsoft 365 admin center →
            Settings → Org Settings → Services → Reports → uncheck <em>“Conceal user, group, and site names in all
            reports”</em>, then re-scan here.</p>
        </div>

        <div className="field" style={{ maxWidth: 320 }}>
          <label htmlFor="so-filter">Filter sites by URL (optional)</label>
          <input id="so-filter" placeholder="e.g. /sites/HR — leave blank for all"
            value={nameFilter} disabled={busy}
            onChange={(e) => setNameFilter(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void scan(); }} />
        </div>

        <div className="gate__cta" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn--primary" onClick={() => void scan()} disabled={busy}>
            {busy ? 'Working…' : result ? 'Re-scan' : nameFilter.trim() ? 'List owners for matching sites' : 'List owners for all sites'}
          </button>
          {busy && <button className="btn btn--ghost" onClick={() => { cancelRef.current = true; }}>Stop</button>}
        </div>

        {siteOwnersOptedIn ? (
          <p className="small muted" style={{ marginTop: 12 }}>
            SharePoint <strong>Site Owners</strong> column enabled (<code>Sites.FullControl.All</code>, read-only).
            {result ? ' Re-scan to populate it.' : ''}
          </p>
        ) : (
          <div className="notice notice--info" style={{ marginTop: 12 }}>
            <strong>Add a SharePoint “Site Owners” column?</strong>
            <p>The list above already shows each site's <em>primary</em> owner from the report. To also list the
              identities on each site's SharePoint permissions (its “Owners” group), enable the add-on — it reads site
              permissions, which needs <code>Sites.FullControl.All</code> (broad, tenant-wide, write-capable; used
              read-only here). You'll consent on the next scan.</p>
            <button className="btn btn--secondary btn--sm" onClick={() => grantSiteOwners('C15')} disabled={busy}>
              Enable Site Owners column
            </button>
          </div>
        )}

        {status === 'enumerating' && (
          <p className="small muted" style={{ marginTop: 16 }}>Pulling the SharePoint site usage report…</p>
        )}

        {status === 'scanning' && (
          <div style={{ marginTop: 16 }}>
            <ProgressBar value={done} max={total} label={siteOwnersOptedIn ? 'Reading site owners' : 'Building list'} />
          </div>
        )}

        {status === 'error' && <ErrorNotice error={error} />}

        {result && (
          <>
            <MetricGrid>
              <Metric label="Sites found" value={result.sitesScanned} sub="All SharePoint sites" />
              <Metric label="Sites with no owner" value={result.sitesWithNoOwner}
                tone={result.sitesWithNoOwner > 0 ? 'red' : 'green'} sub="No owner in the report" />
              <Metric label="Owner assignments" value={result.totalOwnerAssignments} sub="Across all sites" />
              <Metric label="Distinct owners" value={result.distinctOwners} sub="De-duplicated identities" />
            </MetricGrid>

            <div className="panel__head" style={{ marginTop: 8 }}>
              <h3 className="metric__label">Owners by site</h3>
              <button className="btn btn--secondary btn--sm" onClick={exportCsv}>Export CSV</button>
            </div>

            <table className="matrix">
              <thead>
                <tr>
                  <th>Site</th>
                  <th>Owner (primary)</th>
                  {siteOwnersOptedIn && <th>Site Owners (SharePoint)</th>}
                </tr>
              </thead>
              <tbody>
                {result.records.slice(0, 200).map((rec) => (
                  <tr key={rec.site.id}>
                    <td className="matrix__label">
                      {rec.site.webUrl
                        ? <a href={rec.site.webUrl} target="_blank" rel="noreferrer">{rec.site.displayName}</a>
                        : rec.site.displayName}
                    </td>
                    <td className="matrix__detail">{ownerChips(rec.owners)}</td>
                    {siteOwnersOptedIn && <td className="matrix__detail">{ownerChips(rec.siteOwners)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
            {result.records.length > 200 && (
              <p className="small muted" style={{ marginTop: 8 }}>
                Showing first 200 of {result.records.length} sites — export CSV for the full list.
              </p>
            )}

            <p className="small muted" style={{ marginTop: 12 }}>
              Sites come from the SharePoint site usage report (Reports.Read.All), which covers every site — group and
              non-group. <strong>Owner (primary)</strong> is the report's site owner.
              {siteOwnersOptedIn && (
                <>
                  {' '}<strong>Site Owners</strong> are the owner-role identities on each site's SharePoint permissions
                  (Sites.FullControl.All, read-only) — often the site's “Owners” group rather than each individual member.
                </>
              )}
              {' '}Deleted sites are excluded. Generated client-side; nothing leaves your browser.
            </p>
          </>
        )}
      </ModuleGate>
    </Panel>
  );
}
